import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import { FileText, Download, Users, TrendingUp, AlertCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Brand colors ──────────────────────────────────────────────────────────────
const C = {
  dark:    '#0e2e2e',
  green:   '#7ab648',
  blue:    '#0078D4',
  ltBlue:  '#50A0D0',
};
const PALETTE = ['#0078D4', '#50A0D0', '#7ab648', '#00B4D8', '#F59E0B', '#8b5cf6', '#ef4444', '#10b981', '#f97316', '#06b6d4'];
const SA_FIXED = { 'AI Workforce': C.blue, 'Security': C.ltBlue };
const getSAColor = (name, i) => SA_FIXED[name] || PALETTE[i % PALETTE.length];

function fmt(n) { return (n ?? 0).toLocaleString(); }
function shortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ── Presets & FY options (matching spec exactly) ──────────────────────────────
const RANGE_PRESETS = [
  { label: 'All Time',    value: 'all' },
  { label: 'Last Week',   value: 'last_week' },
  { label: 'Last Month',  value: 'last_month' },
];
const FY_OPTIONS = [
  { label: 'FY26 (Jul 25 – Jun 26)', value: 'FY26' },
  { label: 'FY25 (Jul 24 – Jun 25)', value: 'FY25' },
  { label: 'FY24 (Jul 23 – Jun 24)', value: 'FY24' },
];

const TABS = [
  { id: 'overview',  label: 'Overview' },
  { id: 'content',   label: 'Content Analytics' },
  { id: 'downloads', label: 'Download Analytics' },
  { id: 'partners',  label: 'Partner Analytics' },
];

// ── Helper components ─────────────────────────────────────────────────────────
function LoadingState() {
  return (
    <div data-testid="compete-loading" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '56px 0', color: '#64748b', fontSize: 14 }}>
      <Loader2 size={18} style={{ animation: 'spin 1s linear infinite', color: C.green }} />
      Loading Compete Hub data…
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div data-testid="compete-error" style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '24px 20px', background: '#fafafa', border: '1px solid #e2e8f0', borderRadius: 10, margin: '16px 0' }}>
      <AlertCircle size={18} color="#94a3b8" style={{ flexShrink: 0, marginTop: 1 }} />
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#475569', marginBottom: 3 }}>Compete Hub data is temporarily unavailable.</div>
        <div style={{ fontSize: 12, color: '#94a3b8' }}>{message || 'Please try again shortly.'}</div>
      </div>
    </div>
  );
}

function KpiTile({ label, value, icon: Icon, accent, testId }) {
  return (
    <div className="stat-card" data-testid={testId} style={{ borderTop: `3px solid ${accent}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="stat-label">{label}</div>
        <Icon size={15} color={accent} />
      </div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

function SolutionAreaBadge({ name, index }) {
  const color = getSAColor(name, index);
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 600, background: `${color}22`, color, whiteSpace: 'nowrap' }}>
      {name || '—'}
    </span>
  );
}

// ── Shared Filter Bar ─────────────────────────────────────────────────────────
function CompeteFilterBar({ timeRange, setTimeRange, fiscalYear, setFiscalYear, dateFrom, setDateFrom, dateTo, setDateTo }) {
  const iStyle = { height: 30, padding: '0 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, color: '#1e293b', background: '#fff', outline: 'none' };

  const handlePreset = (v) => { setTimeRange(v); setFiscalYear(''); setDateFrom(''); setDateTo(''); };
  const handleFY    = (v) => { setFiscalYear(v); setTimeRange(''); setDateFrom(''); setDateTo(''); };

  const activePreset = !fiscalYear && !dateFrom ? timeRange : null;

  return (
    <div data-testid="compete-filter-bar" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '11px 16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 16 }}>
      {/* Range presets */}
      <div style={{ display: 'flex', gap: 4 }}>
        {RANGE_PRESETS.map(p => (
          <button key={p.value} data-testid={`compete-preset-${p.value}`} onClick={() => handlePreset(p.value)}
            style={{ height: 30, padding: '0 12px', fontSize: 12, fontWeight: 600, border: `1px solid ${activePreset === p.value ? C.green : '#e2e8f0'}`, borderRadius: 6, cursor: 'pointer', background: activePreset === p.value ? C.dark : '#fff', color: activePreset === p.value ? C.green : '#64748b', transition: 'all 0.15s' }}>
            {p.label}
          </button>
        ))}
      </div>

      {/* FY selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>FY:</span>
        <select data-testid="compete-filter-fy" value={fiscalYear} onChange={e => handleFY(e.target.value)}
          style={{ ...iStyle, width: 190, cursor: 'pointer' }}>
          <option value="">— Any —</option>
          {FY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* Custom date range */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input type="date" data-testid="compete-date-from" value={dateFrom}
          onChange={e => { setDateFrom(e.target.value); setTimeRange(''); setFiscalYear(''); }}
          style={iStyle} />
        <span style={{ color: '#94a3b8', fontSize: 12 }}>to</span>
        <input type="date" data-testid="compete-date-to" value={dateTo}
          onChange={e => { setDateTo(e.target.value); setTimeRange(''); setFiscalYear(''); }}
          style={iStyle} />
      </div>
    </div>
  );
}

// ── Tab 1 — Overview ─────────────────────────────────────────────────────────
function OverviewTab({ data }) {
  const { summary = {}, download_trend = [], top_assets = [] } = data;

  const trendData = download_trend.map(r => ({ date: shortDate(r.date), downloads: r.downloads }));

  return (
    <>
      {/* 4 KPI tiles */}
      <div className="stats-grid" data-testid="compete-overview-kpis" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', marginBottom: 20 }}>
        <KpiTile label="Total Assets"     value={fmt(summary.total_assets)}     icon={FileText}   accent={C.dark}   testId="kpi-compete-assets" />
        <KpiTile label="Total Downloads"  value={fmt(summary.total_downloads)}  icon={Download}   accent={C.green}  testId="kpi-compete-downloads" />
        <KpiTile label="Total Users"      value={fmt(summary.total_users)}      icon={Users}      accent={C.blue}   testId="kpi-compete-users" />
        <KpiTile label="Active Partners"  value={fmt(summary.active_partners)}  icon={TrendingUp} accent={C.ltBlue} testId="kpi-compete-partners" />
      </div>

      {/* Download Activity area chart */}
      {trendData.length > 0 && (
        <div className="report-card" data-testid="compete-download-trend" style={{ padding: '16px 16px 8px', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 10 }}>Download Activity</div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={trendData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
              <defs>
                <linearGradient id="competeGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={C.dark}  stopOpacity={0.18} />
                  <stop offset="95%" stopColor={C.dark}  stopOpacity={0.01} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} width={44} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e2e8f0' }} formatter={v => [fmt(v), 'Downloads']} />
              <Area type="monotone" dataKey="downloads" name="Downloads" stroke={C.green} strokeWidth={2} fill="url(#competeGrad)" dot={false} activeDot={{ r: 4, fill: C.green }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Top Downloaded Assets table */}
      {top_assets.length > 0 && (
        <div className="report-card" data-testid="compete-top-assets" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px 10px', fontSize: 13, fontWeight: 600, color: '#1e293b', borderBottom: '1px solid #f1f5f9' }}>Top Downloaded Assets</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
                  {['#', 'Asset Title', 'Solution Area', 'Content Type', 'Downloads'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: h === '#' || h === 'Downloads' ? 'center' : 'left', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {top_assets.map((a, i) => (
                  <tr key={a.id || i} style={{ borderBottom: '1px solid #f8fafc' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                    <td style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 700, color: '#94a3b8', width: 36 }}>{i + 1}</td>
                    <td style={{ padding: '10px 16px', fontWeight: 600, color: '#1e293b', maxWidth: 320 }}>{a.title || '—'}</td>
                    <td style={{ padding: '10px 16px' }}><SolutionAreaBadge name={a.solution_area} index={i} /></td>
                    <td style={{ padding: '10px 16px', color: '#64748b', fontSize: 12 }}>{a.content_type || '—'}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 700, color: C.green }}>{fmt(a.downloads)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

// ── Tab 2 — Content Analytics ─────────────────────────────────────────────────
const RADIAN = Math.PI / 180;
function renderPctLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }) {
  if (percent < 0.05) return null;
  const r = innerRadius + (outerRadius - innerRadius) * 0.55;
  return (
    <text x={cx + r * Math.cos(-midAngle * RADIAN)} y={cy + r * Math.sin(-midAngle * RADIAN)}
      fill="#fff" textAnchor="middle" dominantBaseline="central" style={{ fontSize: 11, fontWeight: 700 }}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

function ContentTab({ data }) {
  const { by_solution_area = [], by_content_type = [], by_language = [] } = data;

  const langSorted = [...by_language].sort((a, b) => (b.download_count || 0) - (a.download_count || 0));
  const typeData = by_content_type.map(r => ({ name: r.content_type || '—', Assets: r.asset_count || 0, Downloads: r.download_count || 0 }));
  const langData = langSorted.map(r => ({ language: r.language || '—', Assets: r.asset_count || 0, Downloads: r.download_count || 0 }));

  return (
    <>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
        {/* Donut — by solution area */}
        <div className="report-card" data-testid="compete-content-donut" style={{ flex: '0 0 300px', padding: '16px 16px 8px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 6 }}>Content by Solution Area</div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={by_solution_area.map(r => ({ name: r.solution_area, value: r.download_count || 0, assets: r.asset_count || 0 }))}
                cx="50%" cy="50%" innerRadius={52} outerRadius={90} dataKey="value"
                labelLine={false} label={renderPctLabel}>
                {by_solution_area.map((r, i) => <Cell key={r.solution_area} fill={getSAColor(r.solution_area, i)} />)}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e2e8f0' }}
                formatter={(v, name, props) => [`${fmt(v)} downloads (${fmt(props.payload.assets)} assets)`, props.payload.name]} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }}
                formatter={(value) => <span style={{ color: '#475569' }}>{value}</span>} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Grouped bar — by content type */}
        <div className="report-card" data-testid="compete-content-type-chart" style={{ flex: '1 1 320px', padding: '16px 16px 8px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 6 }}>Content by Type</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={typeData} margin={{ top: 4, right: 12, left: 0, bottom: 48 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} angle={-30} textAnchor="end" interval={0} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} width={44} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e2e8f0' }} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
              <Bar dataKey="Assets"    fill={C.dark}  radius={[2, 2, 0, 0]} maxBarSize={20} />
              <Bar dataKey="Downloads" fill={C.green} radius={[2, 2, 0, 0]} maxBarSize={20} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Horizontal bar — by language */}
      {langData.length > 0 && (
        <div className="report-card" data-testid="compete-language-chart" style={{ padding: '16px 16px 8px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>Content by Language</div>
          <ResponsiveContainer width="100%" height={Math.max(180, langData.length * 30 + 40)}>
            <BarChart data={langData} layout="vertical" margin={{ top: 4, right: 48, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} />
              <YAxis type="category" dataKey="language" tick={{ fontSize: 11, fill: '#475569' }} width={80} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e2e8f0' }} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
              <Bar dataKey="Assets"    fill={C.dark}  radius={[0, 2, 2, 0]} maxBarSize={12} />
              <Bar dataKey="Downloads" fill={C.green} radius={[0, 2, 2, 0]} maxBarSize={12} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  );
}

// ── Tab 3 — Download Analytics ────────────────────────────────────────────────
function DownloadsTab({ data }) {
  const { by_solution_play = [], by_country = [] } = data;

  const sorted  = [...by_solution_play].sort((a, b) => (b.downloads || 0) - (a.downloads || 0));
  const top15   = [...by_country].sort((a, b) => (b.downloads || 0) - (a.downloads || 0)).slice(0, 15);
  const saIndex = {};
  sorted.forEach(r => { if (!(r.solution_area in saIndex)) saIndex[r.solution_area] = Object.keys(saIndex).length; });

  return (
    <>
      {/* Solution play table */}
      <div className="report-card" data-testid="compete-solution-play-table" style={{ overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ padding: '14px 16px 10px', fontSize: 13, fontWeight: 600, color: '#1e293b', borderBottom: '1px solid #f1f5f9' }}>
          Downloads by Solution Area & Play
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
                {['Solution Area', 'Solution Play', 'Downloads', 'Unique Users'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: h === 'Downloads' || h === 'Unique Users' ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f8fafc', borderLeft: i === 0 ? '3px solid #F59E0B' : '3px solid transparent' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                  <td style={{ padding: '10px 16px' }}><SolutionAreaBadge name={row.solution_area} index={saIndex[row.solution_area] || 0} /></td>
                  <td style={{ padding: '10px 16px', color: '#1e293b', maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.solution_play || '—'}</td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 700, color: C.green }}>{fmt(row.downloads)}</td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', color: '#475569' }}>{fmt(row.unique_users)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Country bar chart */}
      {top15.length > 0 && (
        <div className="report-card" data-testid="compete-country-chart" style={{ padding: '16px 16px 8px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 10 }}>Downloads by Country (Top {top15.length})</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={top15.map(r => ({ country: r.country, downloads: r.downloads }))} margin={{ top: 4, right: 16, left: 0, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="country" tick={{ fontSize: 10, fill: '#64748b' }} angle={-40} textAnchor="end" interval={0} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} width={48} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e2e8f0' }} formatter={v => [fmt(v), 'Downloads']} />
              <Bar dataKey="downloads" name="Downloads" fill={C.dark} radius={[3, 3, 0, 0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  );
}

// ── Tab 4 — Partner Analytics ─────────────────────────────────────────────────
const RANK_COLORS = ['#F59E0B', '#94A3B8', '#D97706'];

function PartnersTab({ data }) {
  const { by_organization = [] } = data;
  const [search, setSearch] = useState('');

  const sorted = [...by_organization].sort((a, b) => (b.total_downloads || 0) - (a.total_downloads || 0));
  const filtered = search.trim()
    ? sorted.filter(r => (r.domain || '').toLowerCase().includes(search.trim().toLowerCase()))
    : sorted;

  return (
    <>
      <div className="report-card" data-testid="compete-partners-table" style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 12px', borderBottom: '1px solid #f1f5f9' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
            Downloads by Partner Organization
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400, marginLeft: 8 }}>{filtered.length} org{filtered.length !== 1 ? 's' : ''}</span>
          </span>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ position: 'absolute', left: 9, width: 13, height: 13, color: '#94a3b8', pointerEvents: 'none' }}>
              <circle cx="8.5" cy="8.5" r="5.5" /><path d="M15 15l-3.5-3.5" />
            </svg>
            <input
              type="text"
              data-testid="partner-domain-search"
              placeholder="Search by domain…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ height: 32, paddingLeft: 28, paddingRight: 10, border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, color: '#1e293b', background: '#fff', outline: 'none', width: 190 }}
              onFocus={e => { e.target.style.borderColor = C.green; }}
              onBlur={e => { e.target.style.borderColor = '#e2e8f0'; }}
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
            {search ? `No organisations match "${search}".` : 'No partner data available.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
                  {['#', 'Organisation Domain', 'Total Downloads', 'Unique Users'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: h === '#' || h === 'Total Downloads' || h === 'Unique Users' ? 'center' : 'left', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, i) => {
                  const rank  = sorted.indexOf(row);
                  const rankC = rank < 3 ? RANK_COLORS[rank] : 'transparent';
                  return (
                    <tr key={i} data-testid={`partner-org-row-${i}`}
                      style={{ borderBottom: '1px solid #f8fafc', borderLeft: `3px solid ${rankC}` }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                      <td style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 700, color: rankC !== 'transparent' ? rankC : '#94a3b8', fontSize: 14 }}>{rank + 1}</td>
                      <td style={{ padding: '10px 16px', fontFamily: 'monospace', color: '#1e293b', fontWeight: 600 }}>{row.domain || '—'}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 700, color: C.green }}>{fmt(row.total_downloads)}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'center', color: '#475569' }}>{fmt(row.unique_users)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function CompeteHubReport() {
  const [activeTab, setActiveTab] = useState('overview');

  // Filter state
  const [timeRange,   setTimeRange]   = useState('all');
  const [fiscalYear,  setFiscalYear]  = useState('');
  const [dateFrom,    setDateFrom]    = useState('');
  const [dateTo,      setDateTo]      = useState('');

  // Per-tab data
  const [overviewData,   setOverviewData]   = useState(null);
  const [overviewLoading,setOverviewLoading]= useState(false);
  const [overviewError,  setOverviewError]  = useState(null);

  const [contentData,    setContentData]    = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentError,   setContentError]   = useState(null);

  const [downloadsData,   setDownloadsData]   = useState(null);
  const [downloadsLoading,setDownloadsLoading]= useState(false);
  const [downloadsError,  setDownloadsError]  = useState(null);

  const [partnersData,   setPartnersData]   = useState(null);
  const [partnersLoading,setPartnersLoading]= useState(false);
  const [partnersError,  setPartnersError]  = useState(null);

  const buildParams = useCallback(() => {
    const p = {};
    if (fiscalYear)             p.fiscal_year = fiscalYear;
    else if (timeRange !== 'all') p.time_range = timeRange;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo)   p.date_to   = dateTo;
    return p;
  }, [timeRange, fiscalYear, dateFrom, dateTo]);

  const loadTab = useCallback(async (tab, params) => {
    const config = {
      overview:  { set: setOverviewData,  setL: setOverviewLoading,  setE: setOverviewError },
      content:   { set: setContentData,   setL: setContentLoading,   setE: setContentError },
      downloads: { set: setDownloadsData, setL: setDownloadsLoading, setE: setDownloadsError },
      partners:  { set: setPartnersData,  setL: setPartnersLoading,  setE: setPartnersError },
    }[tab];
    if (!config) return;
    config.setL(true);
    config.setE(null);
    try {
      const { data } = await axios.get(`${API}/api/lup/v2/report/compete-hub/${tab}`, { params, withCredentials: true });
      if (data?.success === false) {
        config.setE(data.message || 'Data unavailable');
        config.set(null);
      } else {
        config.set(data);
      }
    } catch (e) {
      const msg = e.response?.data?.message || 'Compete Hub data is temporarily unavailable. Please try again shortly.';
      config.setE(msg);
      config.set(null);
    } finally {
      config.setL(false);
    }
  }, []);

  // Load on tab switch
  useEffect(() => {
    loadTab(activeTab, buildParams());
  }, [activeTab]); // eslint-disable-line

  // Reload when filters change
  useEffect(() => {
    loadTab(activeTab, buildParams());
  }, [timeRange, fiscalYear, dateFrom, dateTo]); // eslint-disable-line

  const tabState = {
    overview:  { data: overviewData,  loading: overviewLoading,  error: overviewError },
    content:   { data: contentData,   loading: contentLoading,   error: contentError },
    downloads: { data: downloadsData, loading: downloadsLoading, error: downloadsError },
    partners:  { data: partnersData,  loading: partnersLoading,  error: partnersError },
  };
  const { data, loading, error } = tabState[activeTab];

  const renderContent = () => {
    if (loading) return <LoadingState />;
    if (error)   return <ErrorState message={error} />;
    if (!data)   return null;

    if (activeTab === 'overview')  return <OverviewTab  data={data} />;
    if (activeTab === 'content')   return <ContentTab   data={data} />;
    if (activeTab === 'downloads') return <DownloadsTab data={data} />;
    if (activeTab === 'partners')  return <PartnersTab  data={data} />;
    return null;
  };

  return (
    <div data-testid="compete-hub-report" style={{ padding: '0 0 32px' }}>
      {/* Page header */}
      <div style={{ padding: '20px 24px 0', marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 2 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0e2e2e', letterSpacing: '-0.5px', margin: 0 }}>Compete Content Hub</h1>
          <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>Microsoft Compete Asset Analytics</span>
        </div>
        <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>Download activity and content engagement across the LevelUp partner network.</p>
      </div>

      <div style={{ padding: '16px 24px 0' }}>
        {/* Filter bar */}
        <CompeteFilterBar
          timeRange={timeRange}     setTimeRange={setTimeRange}
          fiscalYear={fiscalYear}   setFiscalYear={setFiscalYear}
          dateFrom={dateFrom}       setDateFrom={setDateFrom}
          dateTo={dateTo}           setDateTo={setDateTo}
        />

        {/* Tab bar */}
        <div data-testid="compete-tabs" style={{ borderBottom: '2px solid #e2e8f0', display: 'flex', gap: 0, marginBottom: 20, overflowX: 'auto' }}>
          {TABS.map(t => (
            <button key={t.id} data-testid={`compete-tab-${t.id}`} onClick={() => setActiveTab(t.id)}
              style={{ padding: '8px 20px', fontSize: 13, fontWeight: 600, border: 'none', background: 'none', cursor: 'pointer', whiteSpace: 'nowrap', borderBottom: activeTab === t.id ? `2px solid ${C.green}` : '2px solid transparent', color: activeTab === t.id ? C.dark : '#64748b', marginBottom: -2, transition: 'color 0.15s' }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div data-testid={`compete-tab-content-${activeTab}`}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
